import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { useBeManualFetcher } from "../../hooks/useBeManualFetcher";
import { useAuthContext } from "../../hooks/useAuthContext";
import { USER_ROLES } from "../../utilities/constants";
import userService from "../../services/userService";
import UserDetailsPageHeader from "../../components/userPage/userDetailsPage/UserDetailsPageHeader";
import UserInfoMainBlock from "../../components/userPage/userDetailsPage/UserInfoMainBlock";
import UserEditModal from "../../components/userPage/userDetailsPage/UserEditModal";
import ConfirmationModal from "../../components/modals/ConfirmationModal";

const canManageUsers = (roles) =>
  roles?.some((r) => r === USER_ROLES.DEVELOPER || r === USER_ROLES.ADMIN) ??
  false;

const UserDetailsPage = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthContext();
  const [user, setUser] = useState(state);
  const showManageActions = canManageUsers(currentUser?.roles);
  const [onFetch, isLoading] = useBeManualFetcher();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (!id) return;

    onFetch({
      action: () => userService.getUserById(id),
      onLoad: (response) => setUser(response),
      actionWhenError: () => setUser(null),
      fetchingItemId: id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const displayName = user
    ? [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
      user?.username ||
      user?.email ||
      ""
    : "Not found";

  const isDeleteLoading =
    typeof isLoading === "object" && isLoading?.id === "delete-user"
      ? isLoading?.isLoading
      : false;
  const isUpdateLoading =
    typeof isLoading === "object" && isLoading?.id === "update-user"
      ? isLoading?.isLoading
      : false;

  const handleUserUpdate = async (body) => {
    return await onFetch({
      action: () => userService.updateUser(body),
      onLoad: (response) => {
        setUser(response);
        setShowEditModal(false);
      },
      successMessage: "User updated successfully",
      fetchingItemId: "update-user",
    });
  };

  const handleDelete = async () => {
    if (!id) return;

    return await onFetch({
      action: () => userService.removeUser(id),
      onLoad: () => {
        setShowDeleteModal(false);
        navigate("/users");
      },
      successMessage: "User deleted successfully",
      fetchingItemId: "delete-user",
    });
  };

  return (
    <>
      <Head title="User Details" />
      <Content>
        <UserDetailsPageHeader
          name={displayName}
          createdDate={user?.createdAt}
          goBackPath="/users"
          onEditClick={() => setShowEditModal(true)}
          onDeleteClick={() => setShowDeleteModal(true)}
          isDeleteLoading={isDeleteLoading}
          canManageUsers={showManageActions}
        />
        {isLoading ? null : user ? (
          <UserInfoMainBlock user={user} />
        ) : (
          <div className="card card-inner">
            <p className="text-soft mb-0">User not found.</p>
          </div>
        )}
        <UserEditModal
          showModal={showEditModal}
          user={user}
          onClose={() => setShowEditModal(false)}
          onSave={handleUserUpdate}
          isSaving={isUpdateLoading}
        />
        <ConfirmationModal
          showModal={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          modalTitle="Do you really want to delete this user?"
          confirmTitle="Delete"
          confirmColor="danger"
          isLoading={isDeleteLoading}
        />
      </Content>
    </>
  );
};

export default UserDetailsPage;
